import React, { useState } from 'react';
import { Modal, Tag, Spin, Alert, Collapse } from 'antd';
import { CloseOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { useGetCateringCategoryProductsByCatIdQuery } from '@/services/api/event/cateringCategoryApi';
import "./eventModal.css"
/**
 * View Event Modal Component
 * Displays event details in a read-only format
 */
const ViewEventModal = ({
  visible,
  onCancel,
  eventData
}) => {
  // State for accordion
  const [activePanels, setActivePanels] = useState([]);

  // Fetch category products for this package
  const {
    data: categoryProducts,
    isLoading: isLoadingProducts,
    error: productsError
  } = useGetCateringCategoryProductsByCatIdQuery(eventData?.id, {
    skip: !eventData?.id || !visible
  });

  // Debug logging to see the API response structure
  // console.log('ViewEventModal - Package ID:', eventData?.id);
  // console.log('ViewEventModal - Full API Response:', categoryProducts);
  // console.log('ViewEventModal - Catering Categories:', categoryProducts?.catering_categories);
  // console.log('ViewEventModal - Loading:', isLoadingProducts);
  // console.log('ViewEventModal - Error:', productsError);

  // Extract catering categories from the API response
  const cateringCategories = categoryProducts?.catering_categories || [];

  // Calculate total price of all products
  const calculateTotalPrice = () => {
    if (!cateringCategories || cateringCategories.length === 0) return 0;

    return cateringCategories.reduce((total, category) => {
      if (category.catering_products && Array.isArray(category.catering_products)) {
        const categoryTotal = category.catering_products.reduce((catTotal, product) => {
          return catTotal + parseFloat(product.price || 0);
        }, 0);
        return total + categoryTotal;
      }
      return total;
    }, 0);
  };

  const totalPrice = calculateTotalPrice();

  // ✅ Determine if package is active
  const isActive = eventData?.status === 1 || eventData?.status === "Active";

  if (!eventData) return null;

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={onCancel}
      footer={null}
      width={700}
      destroyOnClose
      closable={false}
      centered
      className="view-package-modal"
    >
      {/* Fixed Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-gray-800">{eventData.name}</h2>
              {/* ✅ Status Badge beside the name */}
              <Tag
                color={isActive ? 'green' : 'default'}
                className="px-3 py-1 text-xs font-medium"
              >
                {isActive ? 'ACTIVE' : 'INACTIVE'}
              </Tag>
            </div>
            <p className="text-sm text-gray-500 mt-1">View package details and products</p>
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors ml-4"
          >
            <CloseOutlined className="text-lg" />
          </button>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="overflow-y-auto px-6 py-6" style={{ maxHeight: '70vh' }}>
        <div className="space-y-5">
          {/* Package Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Package Name
            </label>
            <div className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 h-11 flex items-center">
              {eventData.name}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <div className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 min-h-[44px] flex items-center">
              {eventData.description || 'No description provided'}
            </div>
          </div>
       
          {/* Number of Guests */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Guests
            </label>
            <div className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 h-11 flex items-center">
              {eventData.minimum_serving_size === eventData.maximum_serving_size
                ? `${eventData.minimum_serving_size} (Fixed)`
                : `${eventData.minimum_serving_size} - ${eventData.maximum_serving_size} (Range)`
              }
            </div>
          </div>

          {/* Total Package Price */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Package Price
            </label>
            <div className="w-full px-3 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-900 h-11 flex items-center">
              <span className="text-lg font-semibold text-orange-600">
                ₦{totalPrice.toLocaleString()}.00
              </span>
              <span className="text-sm text-gray-500 ml-2">
                (sum of all products)
              </span>
            </div>
          </div>

          {/* Package Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Package Image
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center bg-gray-50">
              <div className="flex justify-center">
                <div className="relative">
                  {eventData.url ? (
                    <img
                      src={eventData.url}
                      alt={eventData.name}
                      className="w-20 h-20 rounded-lg object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div
                    className="w-20 h-20 rounded-lg bg-gray-200 flex items-center justify-center"
                    style={{ display: eventData.url ? 'none' : 'flex' }}
                  >
                    <span className="text-gray-400 text-2xl">📷</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Category Products Section */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <label className="text-sm font-medium text-gray-700">
                Package Products
              </label>
              {cateringCategories && Array.isArray(cateringCategories) && cateringCategories.length > 0 && (
                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                  {cateringCategories.length}
                </span>
              )}
            </div>

            {isLoadingProducts ? (
              <div className="flex justify-center py-8">
                <Spin size="large" />
              </div>
            ) : productsError ? (
              <Alert
                message="Error loading products"
                description={productsError?.data?.message || 'Failed to load package products'}
                type="error"
                showIcon
              />
            ) : cateringCategories && Array.isArray(cateringCategories) && cateringCategories.length > 0 ? (
              <Collapse
                activeKey={activePanels}
                onChange={setActivePanels}
                expandIcon={({ isActive }) => isActive ? <UpOutlined /> : <DownOutlined />}
                expandIconPosition="end"
                ghost
                className="bg-transparent"
                items={cateringCategories.map((category, categoryIndex) => ({
                  key: category.id || categoryIndex,
                  label: (
                    <div className="flex items-center gap-3 py-1">
                      <div className="w-6 h-6 border-2 border-orange-500 rounded-full flex items-center justify-center">
                        <span className="text-orange-500 text-xs font-semibold">✓</span>
                      </div>
                      <span className="text-sm font-medium text-gray-900">{category.name}</span>
                    </div>
                  ),
                  children: (
                    <div className="pt-2 pb-2">
                      {category.catering_products && Array.isArray(category.catering_products) && category.catering_products.length > 0 ? (
                        <div className="ml-9 space-y-1">
                          {category.catering_products.map((product, productIndex) => (
                            <div key={product.id || productIndex} className="flex justify-between items-center py-1">
                              <div className="flex items-center gap-3">
                                <div className="w-5 h-5 border-2 border-orange-500 rounded-full flex items-center justify-center">
                                  <span className="text-orange-500 text-xs font-semibold">{productIndex + 1}</span>
                                </div>
                                <span className="text-sm text-gray-900">{product.name}</span>
                              </div>
                              <span className="text-sm font-semibold text-gray-700">
                                ₦{parseFloat(product.price).toLocaleString()}.00
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="ml-9 text-center py-4 text-gray-500 text-sm">
                          No products in this category
                        </div>
                      )}
                    </div>
                  ),
                }))}
              />
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <div className="text-4xl mb-3">📦</div>
                <h5 className="text-lg font-medium text-gray-900 mb-2">No Products Added</h5>
                <p className="text-gray-500">This package doesn't have any products yet</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Fixed Footer */}
      <div className="bg-white border-t border-gray-200 px-6 py-4">
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium h-11"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ViewEventModal;